// The sections of one bet's detail panel (CMS Lịch sử cược), from the API body of GET /api/admin/bets/detail
// (cms/apps/api/src/lib/bet-chi-tiet.ts). Pure: the panel only draws what this returns.
import { formatVnd, formatVnDateTime } from '@ruby-cms/shared';

export type BetDetail = {
  key: string; nguon: string; ref: string; user_id: number; user_name: string | null;
  game_id: string; game_name: string; sid: string; created_time: number;
  tien: 'vin' | 'xu' | 'xeng' | 'tron' | null; stake: number; payout: number; refund: number; net: number;
  ket_qua: 'cho' | 'thang' | 'thua' | 'hoa' | 'hoan'; trang_thai: 'Running' | 'Done'; edited_payout: number | null; chi_tiet: string;
  vong: null | {
    id: string; mo_luc: number | null; dong_luc: number | null; ket_qua: string | null; xuc_xac: number[] | null; tong: number | null;
    them: { ten: string; gia_tri: string }[];
    ghe: { ghe: number | null; ten: string; gop: number; nhan: number; lai: number; bai: string }[] | null; bot: number;
  };
  cua: { ten: string; cuoc: number | null; thang: number | null }[];
  so_du: null | { truoc: number; sau: number; nguon: 'so_giao_dich' | 'nha_cung_cap'; pham_vi: 'van' | 'dat_cuoc' };
  so_du_ly_do: string | null;
  nha_cung_cap: null | {
    nha: string; game: string; ma_van: string; ma_giao_dich: string;
    legs: { luc: number; loai: string; ket_qua: string; so_tien: number; so_du_truoc: number | null; so_du_sau: number | null }[];
  };
  canh_bao: string[];
};

export type Section = {
  id: string;
  title: string;
  kv?: [string, string][];
  table?: { head: string[]; rows: string[][] };
  note?: string;
  warn?: boolean;
};

export const KET_QUA: Record<BetDetail['ket_qua'], [string, 'blue' | 'green' | 'red' | 'gray' | 'teal']> = {
  cho: ['Đang chờ', 'blue'], thang: ['Thắng', 'green'], thua: ['Thua', 'red'], hoa: ['Hòa', 'gray'], hoan: ['Hoàn', 'teal'],
};

const unit = (t: BetDetail['tien']) => (t === 'xu' ? ' xu' : t === 'xeng' ? ' xèng' : '');
const money = (n: number | null | undefined, t: BetDetail['tien'] = null, signed = false) =>
  n === null || n === undefined ? '-' : `${signed && n > 0 ? '+' : ''}${formatVnd(Math.round(n))}${unit(t)}`;
const when = (ms: number | null) => (ms ? formatVnDateTime(new Date(ms)) : '-');

export function betDetailView(b: BetDetail): { title: string; subtitle: string; tag: [string, 'blue' | 'green' | 'red' | 'gray' | 'teal']; sections: Section[] } {
  const out: Section[] = [];
  if (b.canh_bao.length) out.push({ id: 'canh-bao', title: 'Lưu ý', note: b.canh_bao.join('\n'), warn: true });

  out.push({
    id: 'van', title: 'Ván',
    kv: [
      ['Game', b.game_name],
      ['Mã phiên', b.sid],
      ['Người chơi', `${b.user_name ?? 'Mã ' + b.user_id} (mã ${b.user_id})`],
      ['Lúc ghi cược', when(b.created_time)],
      ['Trạng thái', b.trang_thai === 'Running' ? 'Đang chạy' : 'Đã xong'],
    ],
  });

  const v = b.vong;
  if (v) {
    const kv: [string, string][] = [];
    if (v.ket_qua) kv.push(['Kết quả', v.ket_qua]);
    if (v.xuc_xac) kv.push(['Xúc xắc', `${v.xuc_xac.join(' - ')} (tổng ${v.tong})`]);
    if (v.mo_luc) kv.push(['Mở ván', when(v.mo_luc)]);
    if (v.dong_luc) kv.push(['Chốt ván', when(v.dong_luc)]);
    for (const t of v.them) kv.push([t.ten, t.gia_tri]);
    if (kv.length) out.push({ id: 'vong', title: 'Kết quả ván', kv });
    if (v.ghe?.length) {
      out.push({
        id: 'ghe', title: 'Người ngồi bàn',
        table: { head: ['Ghế', 'Người chơi', 'Góp', 'Nhận', 'Lời / lỗ', 'Bài (số)'], rows: v.ghe.map((s) => [s.ghe === null ? '-' : String(s.ghe), s.ten, money(s.gop), money(s.nhan), money(s.lai, null, true), s.bai || '-']) },
        note: v.bot ? `${v.bot} ghế bot nhà` : undefined,
      });
    }
  } else if (b.nguon !== 'miracle') {
    out.push({ id: 'vong', title: 'Kết quả ván', note: b.chi_tiet || 'Ván này không có bản ghi kết quả riêng.' });
  }

  if (b.cua.length) {
    out.push({
      id: 'cua', title: 'Cửa đã đặt',
      table: { head: ['Cửa', 'Cược', 'Nhận về'], rows: b.cua.map((c) => [c.ten, money(c.cuoc, b.tien), b.ket_qua === 'cho' ? '-' : money(c.thang, b.tien)]) },
    });
  }

  const tien: [string, string][] = [
    ['Tiền cược', money(b.stake, b.tien)],
    ['Nhận về', b.ket_qua === 'cho' ? '-' : money(b.payout, b.tien)],
  ];
  if (b.refund > 0) tien.push(['Hoàn lại', money(b.refund, b.tien)]);
  tien.push(['Lời / lỗ', b.ket_qua === 'cho' ? '-' : money(b.net, b.tien, true)]);
  if (b.so_du) {
    const lab = b.so_du.pham_vi === 'dat_cuoc' ? 'khi đặt cược' : '';
    tien.push([`Số dư trước ${lab}`.trim(), money(b.so_du.truoc)], [`Số dư sau ${lab}`.trim(), money(b.so_du.sau)]);
  }
  out.push({
    id: 'tien', title: 'Tiền', kv: tien,
    note: b.so_du
      ? (b.so_du.nguon === 'nha_cung_cap' ? 'Số dư do nhà cung cấp gửi kèm ván.' : 'Số dư tính lại từ sổ giao dịch của người chơi không phải số lưu sẵn.')
      : `Số dư trước / sau: không xác định. ${b.so_du_ly_do ?? ''}`.trim(),
  });

  const p = b.nha_cung_cap;
  if (p) {
    out.push({
      id: 'ncc', title: 'Bản ghi nhà cung cấp',
      kv: [['Nhà cung cấp', p.nha], ['Game', p.game], ['Mã ván', p.ma_van], ['Mã giao dịch', p.ma_giao_dich]],
      table: {
        head: ['Lúc', 'Loại', 'Kết quả', 'Tiền', 'Số dư trước', 'Số dư sau'],
        rows: p.legs.map((l) => [when(l.luc), l.loai, l.ket_qua, money(l.so_tien, null, true), money(l.so_du_truoc), money(l.so_du_sau)]),
      },
    });
  }

  return {
    title: `${b.game_name} · phiên ${b.sid}`,
    subtitle: `${b.user_name ?? 'Mã ' + b.user_id} · ${when(b.created_time)}`,
    tag: KET_QUA[b.ket_qua] ?? ['?', 'gray'],
    sections: out,
  };
}
